import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { debounceTime, filter, Observable, Subscription } from 'rxjs';
import { environment } from '../environments/environment'; // Import environment version
import { DialogComponentService } from './components/dialog/dialog.component.service';
import { AuthService } from './core/auth.service';
import { GTMServiceFactory } from './core/google-tag-manager';
import { loadAppVersion } from './store/app.actions';
import { selectAppVersion } from './store/app.selectors'; // Selector to get appVersion from the store
import { AppState } from './store/app.state'; // Import AppState interface

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  appVersion$: Observable<string> | undefined;
  private navigationSubscription: Subscription | undefined;
  private lastNavigationUrl: string = '';

  constructor(
    private store: Store<AppState>,
    private messageService: MessageService,
    private dialogService: DialogComponentService,
    private authService: AuthService,
    private router: Router,
    private gtmService: GTMServiceFactory
  ) {
    this.store.dispatch(loadAppVersion());
  }

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      this.gtmService.initializeFromUserInfo();
    }
    this.navigationSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Only handle if URL has changed (to avoid multiple triggers on same page)
        if (event.url !== this.lastNavigationUrl) {
          this.lastNavigationUrl = event.url;
          this.handlePageChange();
        }
      });
  }

  ngAfterViewInit() {
    // For already logged-in users who refresh the page
    this.handlePageRefresh();

    // Version check logic
    const currentAppVersion = environment.appVersion;
    this.appVersion$ = this.store.select(selectAppVersion);
    this.appVersion$
      .pipe(
        debounceTime(500), // Wait for 500ms to stabilize state changes
        filter(version => version !== environment.appVersion)
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            key: 'message',
            data: {
              title: 'New Version Available!',
              body: `Your application version (${currentAppVersion}) is outdated. Please refresh to update the version.`,
            },
          });
        },
        error: err => {
          console.error('Error in app version subscription:', err);
        },
      });
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  /**
   * Handle page refresh for already logged-in users
   * This ensures the banner shows on refresh if the user hasn't seen it yet
   */
  private handlePageRefresh(): void {
    this.showBannerIfNeeded();
  }

  /**
   * Handle page navigation within the application
   * Shows the banner when user navigates between pages if they haven't seen it yet
   */
  private handlePageChange(): void {
    this.showBannerIfNeeded();
  }

  /**
   * Helper method to show the banner if user is logged in and hasn't seen it yet
   * Used by both page refresh and page navigation handlers
   */
  private showBannerIfNeeded(): void {
    if (this.authService.isLoggedIn()) {
      const userInfo = this.authService.getUserInfo();
      const email = userInfo?.email || this.authService.getEmail();
      if (email) {
        // Short delay to ensure UI is ready
        setTimeout(() => {
          this.dialogService.openComingSoonBanner(email);
        }, 300);
      }
    }
  }
}
